/**
 * PromptEditorModal Component
 * Issue #417: Modal for viewing and editing AI prompts
 */

import { useState, useCallback } from 'react';

interface PromptEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  systemPrompt: string;
  guidelines: string;
  customGuidelines?: string;
  /** Issue #426: Full assembled prompt including all context */
  fullPrompt?: string;
  onSave: (customPrompt: string | null, customGuidelines: string | null) => void;
}

export function PromptEditorModal({
  isOpen,
  onClose,
  systemPrompt,
  guidelines,
  customGuidelines = '',
  fullPrompt,
  onSave,
}: PromptEditorModalProps) {
  const [editedGuidelines, setEditedGuidelines] = useState(customGuidelines);
  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showFullPrompt, setShowFullPrompt] = useState(false);

  const handleSave = useCallback(() => {
    onSave(
      useCustomPrompt ? customPrompt : null,
      editedGuidelines || null
    );
    onClose();
  }, [useCustomPrompt, customPrompt, editedGuidelines, onSave, onClose]);

  const handleReset = useCallback(() => {
    setEditedGuidelines(customGuidelines);
    setUseCustomPrompt(false);
    setCustomPrompt('');
  }, [customGuidelines]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-xl">📝</span>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Edit AI Prompt
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* System Prompt (Read-only) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                System Prompt (Read-only)
              </label>
              <span className="text-xs text-slate-500">
                This defines the AI's role and behavior
              </span>
            </div>
            <pre className="p-4 bg-slate-100 dark:bg-slate-900 rounded-lg text-xs text-slate-700 dark:text-slate-300 overflow-auto max-h-40 font-mono whitespace-pre-wrap">
              {systemPrompt}
            </pre>
          </div>

          {/* Default Guidelines (Read-only) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Default Guidelines (Read-only)
              </label>
            </div>
            <pre className="p-4 bg-slate-100 dark:bg-slate-900 rounded-lg text-xs text-slate-700 dark:text-slate-300 overflow-auto max-h-32 font-mono whitespace-pre-wrap">
              {guidelines}
            </pre>
          </div>

          {/* Custom Guidelines (Editable) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Custom Guidelines (Editable)
              </label>
              <span className="text-xs text-slate-500">
                Add your own instructions for the AI
              </span>
            </div>
            <textarea
              value={editedGuidelines}
              onChange={(e) => setEditedGuidelines(e.target.value)}
              placeholder="Add custom guidelines here... (e.g., 'Use technical terminology', 'Focus on user experience')"
              className="w-full p-4 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
              rows={4}
            />
          </div>

          {/* Issue #426: Full Prompt Preview (including ultra-deep context) */}
          {fullPrompt && (
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => setShowFullPrompt(!showFullPrompt)}
                  className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-purple-600 dark:hover:text-purple-400"
                >
                  <span>{showFullPrompt ? '▼' : '▶'}</span>
                  <span>Full Prompt Preview</span>
                  <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded">
                    {Math.round(fullPrompt.length / 4)} tokens
                  </span>
                </button>
                <span className="text-xs text-slate-500">
                  Complete prompt sent to AI (includes all context)
                </span>
              </div>
              {showFullPrompt && (
                <pre className="p-4 bg-slate-100 dark:bg-slate-900 rounded-lg text-xs text-slate-700 dark:text-slate-300 overflow-auto max-h-96 font-mono whitespace-pre-wrap">
                  {fullPrompt}
                </pre>
              )}
            </div>
          )}

          {/* Advanced: Override entire prompt */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={useCustomPrompt}
                onChange={(e) => setUseCustomPrompt(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Override entire prompt (Advanced)
              </span>
              <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 rounded">
                Expert
              </span>
            </label>
            {useCustomPrompt && (
              <div>
                <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                  Warning: This replaces the entire prompt. Make sure to include all necessary instructions.
                </p>
                <textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Enter your complete custom prompt here..."
                  className="w-full p-4 border border-amber-300 dark:border-amber-600 rounded-lg text-sm text-slate-700 dark:text-slate-300 bg-amber-50 dark:bg-amber-900/20 focus:ring-2 focus:ring-amber-500 focus:border-transparent font-mono"
                  rows={8}
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
          >
            Reset to Default
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PromptEditorModal;
